import type { Language, Locale } from '@/config/locales';

export interface PickupMenuTranslation {
  storeLocationSelect: string;
  prepTime: string;
  storeSelection: string;
  cancel: string;
  storeNames: {
    blueLunaCafe: string;
    elPatioTacos: string;
    laCasaCafe: string;
    rinconMexicano: string;
    panaderiaDulceVida: string;
    cantinaLaFiesta: string;
    saborMexico: string;
    tacoExpress: string;
    cafeAroma: string;
    mercadoVerde: string;
  };
  storeStatus: {
    '영업중': string;
    '준비중': string;
    '마감': string;
  };
  categories: {
    appetizers: string;
    mains: string;
    sides: string;
    drinks: string;
    desserts: string;
    uncategorized: string;
    all: string;
  };
  menuItems: {
    tacoClassico: { name: string; description: string; };
    tacoPollo: { name: string; description: string; };
    nachosCheese: { name: string; description: string; };
    guacamole: { name: string; description: string; };
    quesadilla: { name: string; description: string; };
    coke: { name: string; description: string; };
    agua: { name: string; description: string; };
    flan: { name: string; description: string; };
    burrito: { name: string; description: string; };
    salsa: { name: string; description: string; };
  };
  discount: string;
  addToCart: string;
  viewCart: string;
  items: string;
  days: Record<number, string>;
  shortDays: Record<number, string>;
  krw: string;
}

// #512 — Locale 단일 출처 정합.
export type PickupMenuTranslations =
  & Record<Language, PickupMenuTranslation>
  & Record<Exclude<Locale, Language>, PickupMenuTranslation>;

export const menuTranslations: PickupMenuTranslations = {
  ko: {
    storeLocationSelect: '매장 위치 선택',
    prepTime: '준비시간',
    storeSelection: '매장 선택',
    cancel: '취소',
    storeNames: {
      blueLunaCafe: '블루 루나 카페',
      elPatioTacos: '엘 파티오 타코스',
      laCasaCafe: '라 카사 델 카페',
      rinconMexicano: '린콘 멕시카노',
      panaderiaDulceVida: '파나데리아 둘체 비다',
      cantinaLaFiesta: '칸티나 라 피에스타',
      saborMexico: '사보르 아 멕시코',
      tacoExpress: '타코 익스프레스',
      cafeAroma: '카페 아로마',
      mercadoVerde: '메르카도 베르데'
    },
    storeStatus: {
      '영업중': '영업중',
      '준비중': '준비중',
      '마감': '마감'
    },
    categories: {
      appetizers: '애피타이저',
      mains: '메인요리',
      sides: '사이드',
      drinks: '음료',
      desserts: '디저트',
      uncategorized: '미분류',
      all: '전체',
    },
    menuItems: {
      tacoClassico: {
        name: '클래식 타코',
        description: '신선한 재료로 만든 전통 멕시칸 타코'
      },
      tacoPollo: {
        name: '치킨 타코',
        description: '구운 치킨과 아보카도가 들어간 타코'
      },
      nachosCheese: {
        name: '치즈 나초',
        description: '바삭한 나초칩과 진한 치즈소스'
      },
      guacamole: {
        name: '과카몰리',
        description: '신선한 아보카도로 만든 딥소스'
      },
      quesadilla: {
        name: '케사디야',
        description: '치즈와 치킨이 들어간 멕시칸 빵'
      },
      coke: {
        name: '코카콜라',
        description: '시원한 탄산음료'
      },
      agua: {
        name: '생수',
        description: '깨끗한 생수'
      },
      flan: {
        name: '플란',
        description: '전통 멕시칸 카라멜 푸딩'
      },
      burrito: {
        name: '부리또',
        description: '쌀, 콩, 고기가 들어간 큰 토르티야 랩'
      },
      salsa: {
        name: '살사',
        description: '신선한 토마토와 향신료로 만든 멕시칸 소스'
      }
    },
    discount: '할인',
    addToCart: '장바구니 담기',
    viewCart: '장바구니 보기',
    items: '개',
    days: {
      0: '일요일',
      1: '월요일',
      2: '화요일',
      3: '수요일',
      4: '목요일',
      5: '금요일',
      6: '토요일'
    },
    shortDays: {
      0: '일',
      1: '월',
      2: '화',
      3: '수',
      4: '목',
      5: '금',
      6: '토'
    },
    krw: '원'
  },
  es: {
    storeLocationSelect: 'Seleccionar Ubicación',
    prepTime: 'Tiempo de preparación',
    storeSelection: 'Selección de Tienda',
    cancel: 'Cancelar',
    storeNames: {
      blueLunaCafe: 'Blue Luna Café',
      elPatioTacos: 'El Patio Tacos',
      laCasaCafe: 'La Casa del Café',
      rinconMexicano: 'Rincón Mexicano',
      panaderiaDulceVida: 'Panadería Dulce Vida',
      cantinaLaFiesta: 'Cantina La Fiesta',
      saborMexico: 'Sabor a México',
      tacoExpress: 'Taco Express',
      cafeAroma: 'Café Aroma',
      mercadoVerde: 'Mercado Verde'
    },
    storeStatus: {
      '영업중': 'Abierto',
      '준비중': 'Preparando',
      '마감': 'Cerrado'
    },
    categories: {
      appetizers: 'Aperitivos',
      mains: 'Platos Principales',
      sides: 'Acompañamientos',
      drinks: 'Bebidas',
      desserts: 'Postres',
      uncategorized: 'Sin categoría',
      all: 'Todos',
    },
    menuItems: {
      tacoClassico: {
        name: 'Taco Clásico',
        description: 'Taco tradicional mexicano con ingredientes frescos'
      },
      tacoPollo: {
        name: 'Taco de Pollo',
        description: 'Taco con pollo asado y aguacate'
      },
      nachosCheese: {
        name: 'Nachos con Queso',
        description: 'Chips crujientes con salsa de queso rica'
      },
      guacamole: {
        name: 'Guacamole',
        description: 'Salsa de aguacate fresco'
      },
      quesadilla: {
        name: 'Quesadilla',
        description: 'Pan mexicano con queso y pollo'
      },
      coke: {
        name: 'Coca-Cola',
        description: 'Refresco carbonatado frío'
      },
      agua: {
        name: 'Agua',
        description: 'Agua purificada'
      },
      flan: {
        name: 'Flan',
        description: 'Pudín tradicional mexicano de caramelo'
      },
      burrito: {
        name: 'Burrito',
        description: 'Tortilla grande rellena de arroz, frijoles y carne'
      },
      salsa: {
        name: 'Salsa',
        description: 'Salsa mexicana fresca de tomate y especias'
      }
    },
    discount: 'Descuento',
    addToCart: 'Añadir al Carrito',
    viewCart: 'Ver Carrito',
    items: 'artículos',
    days: {
      0: 'Domingo',
      1: 'Lunes',
      2: 'Martes',
      3: 'Miércoles',
      4: 'Jueves',
      5: 'Viernes',
      6: 'Sábado'
    },
    shortDays: {
      0: 'Dom',
      1: 'Lun',
      2: 'Mar',
      3: 'Mié',
      4: 'Jue',
      5: 'Vie',
      6: 'Sáb'
    },
    krw: '₩'
  },
  en: {
    storeLocationSelect: 'Select Store Location',
    prepTime: 'Prep time',
    storeSelection: 'Store Selection',
    cancel: 'Cancel',
    storeNames: {
      blueLunaCafe: 'Blue Luna Café',
      elPatioTacos: 'El Patio Tacos',
      laCasaCafe: 'La Casa del Café',
      rinconMexicano: 'Mexican Corner',
      panaderiaDulceVida: 'Sweet Life Bakery',
      cantinaLaFiesta: 'La Fiesta Cantina',
      saborMexico: 'Taste of Mexico',
      tacoExpress: 'Taco Express',
      cafeAroma: 'Aroma Café',
      mercadoVerde: 'Green Market'
    },
    storeStatus: {
      '영업중': 'Open',
      '준비중': 'Preparing',
      '마감': 'Closed'
    },
    categories: {
      appetizers: 'Appetizers',
      mains: 'Main Dishes',
      sides: 'Sides',
      drinks: 'Drinks',
      desserts: 'Desserts',
      uncategorized: 'Uncategorized',
      all: 'All',
    },
    menuItems: {
      tacoClassico: {
        name: 'Classic Taco',
        description: 'Traditional Mexican taco with fresh ingredients'
      },
      tacoPollo: {
        name: 'Chicken Taco',
        description: 'Taco with grilled chicken and avocado'
      },
      nachosCheese: {
        name: 'Cheese Nachos',
        description: 'Crispy nacho chips with rich cheese sauce'
      },
      guacamole: {
        name: 'Guacamole',
        description: 'Fresh avocado dip sauce'
      },
      quesadilla: {
        name: 'Quesadilla',
        description: 'Mexican bread with cheese and chicken'
      },
      coke: {
        name: 'Coca-Cola',
        description: 'Cold carbonated beverage'
      },
      agua: {
        name: 'Water',
        description: 'Purified water'
      },
      flan: {
        name: 'Flan',
        description: 'Traditional Mexican caramel pudding'
      },
      burrito: {
        name: 'Burrito',
        description: 'Large tortilla wrap filled with rice, beans and meat'
      },
      salsa: {
        name: 'Salsa',
        description: 'Fresh Mexican tomato and spice sauce'
      }
    },
    discount: 'Discount',
    addToCart: 'Add to Cart',
    viewCart: 'View Cart',
    items: 'items',
    days: {
      0: 'Sunday',
      1: 'Monday',
      2: 'Tuesday',
      3: 'Wednesday',
      4: 'Thursday',
      5: 'Friday',
      6: 'Saturday'
    },
    shortDays: {
      0: 'Sun',
      1: 'Mon',
      2: 'Tue',
      3: 'Wed',
      4: 'Thu',
      5: 'Fri',
      6: 'Sat'
    },
    krw: '₩'
  },
  pt: {
    storeLocationSelect: 'Selecionar localização', prepTime: 'Tempo de preparo', storeSelection: 'Seleção de loja', cancel: 'Cancelar',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'Aberto', '준비중': 'Preparando', '마감': 'Fechado' },
    categories: { appetizers: 'Aperitivos', mains: 'Pratos principais', sides: 'Acompanhamentos', drinks: 'Bebidas', desserts: 'Sobremesas', uncategorized: 'Sem categoria', all: 'Todos' },
    menuItems: {
      tacoClassico: { name: 'Taco Clássico', description: 'Taco mexicano tradicional com ingredientes frescos' },
      tacoPollo: { name: 'Taco de Frango', description: 'Taco com frango grelhado e abacate' },
      nachosCheese: { name: 'Nachos com Queijo', description: 'Chips crocantes com molho de queijo' },
      guacamole: { name: 'Guacamole', description: 'Molho de abacate fresco' },
      quesadilla: { name: 'Quesadilla', description: 'Pão mexicano com queijo e frango' },
      coke: { name: 'Coca-Cola', description: 'Bebida gaseificada gelada' },
      agua: { name: 'Água', description: 'Água purificada' },
      flan: { name: 'Flan', description: 'Pudim mexicano tradicional de caramelo' },
      burrito: { name: 'Burrito', description: 'Tortilha grande recheada com arroz, feijão e carne' },
      salsa: { name: 'Salsa', description: 'Molho mexicano fresco de tomate e especiarias' },
    },
    discount: 'Desconto', addToCart: 'Adicionar ao carrinho', viewCart: 'Ver carrinho', items: 'itens',
    days: { 0: 'Domingo', 1: 'Segunda-feira', 2: 'Terça-feira', 3: 'Quarta-feira', 4: 'Quinta-feira', 5: 'Sexta-feira', 6: 'Sábado' },
    shortDays: { 0: 'Dom', 1: 'Seg', 2: 'Ter', 3: 'Qua', 4: 'Qui', 5: 'Sex', 6: 'Sáb' }, krw: '₩',
  },
  fr: {
    storeLocationSelect: 'Sélectionner l’emplacement', prepTime: 'Temps de préparation', storeSelection: 'Sélection du magasin', cancel: 'Annuler',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'Ouvert', '준비중': 'En préparation', '마감': 'Fermé' },
    categories: { appetizers: 'Entrées', mains: 'Plats principaux', sides: 'Accompagnements', drinks: 'Boissons', desserts: 'Desserts', uncategorized: 'Non classé', all: 'Tout' },
    menuItems: {
      tacoClassico: { name: 'Taco classique', description: 'Taco mexicain traditionnel avec des ingrédients frais' },
      tacoPollo: { name: 'Taco au poulet', description: 'Taco avec poulet grillé et avocat' },
      nachosCheese: { name: 'Nachos au fromage', description: 'Chips croustillants avec sauce au fromage riche' },
      guacamole: { name: 'Guacamole', description: 'Sauce d’avocat frais' },
      quesadilla: { name: 'Quesadilla', description: 'Pain mexicain au fromage et au poulet' },
      coke: { name: 'Coca-Cola', description: 'Boisson gazeuse fraîche' },
      agua: { name: 'Eau', description: 'Eau purifiée' },
      flan: { name: 'Flan', description: 'Pudding mexicain traditionnel au caramel' },
      burrito: { name: 'Burrito', description: 'Grande tortilla farcie de riz, haricots et viande' },
      salsa: { name: 'Salsa', description: 'Sauce mexicaine fraîche aux tomates et épices' },
    },
    discount: 'Remise', addToCart: 'Ajouter au panier', viewCart: 'Voir le panier', items: 'articles',
    days: { 0: 'Dimanche', 1: 'Lundi', 2: 'Mardi', 3: 'Mercredi', 4: 'Jeudi', 5: 'Vendredi', 6: 'Samedi' },
    shortDays: { 0: 'Dim', 1: 'Lun', 2: 'Mar', 3: 'Mer', 4: 'Jeu', 5: 'Ven', 6: 'Sam' }, krw: '₩',
  },
  zh: {
    storeLocationSelect: '选择门店位置', prepTime: '准备时间', storeSelection: '选择门店', cancel: '取消',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': '营业中', '준비중': '准备中', '마감': '已关门' },
    categories: { appetizers: '开胃菜', mains: '主菜', sides: '配菜', drinks: '饮料', desserts: '甜点', uncategorized: '未分类', all: '全部' },
    menuItems: {
      tacoClassico: { name: '经典塔可', description: '传统墨西哥塔可，配新鲜食材' },
      tacoPollo: { name: '鸡肉塔可', description: '烤鸡和牛油果塔可' },
      nachosCheese: { name: '芝士玉米片', description: '酥脆玉米片配浓郁芝士酱' },
      guacamole: { name: '牛油果酱', description: '新鲜牛油果调味酱' },
      quesadilla: { name: '芝士饼', description: '夹有芝士和鸡肉的墨西哥薄饼' },
      coke: { name: '可口可乐', description: '冰镇汽水' },
      agua: { name: '水', description: '纯净水' },
      flan: { name: '焦糖布丁', description: '传统墨西哥焦糖布丁' },
      burrito: { name: '墨西哥卷', description: '大玉米饼包着米饭、豆子和肉' },
      salsa: { name: '萨尔萨酱', description: '新鲜西红柿和香料调制的墨西哥酱' },
    },
    discount: '折扣', addToCart: '加入购物车', viewCart: '查看购物车', items: '件',
    days: { 0: '星期日', 1: '星期一', 2: '星期二', 3: '星期三', 4: '星期四', 5: '星期五', 6: '星期六' },
    shortDays: { 0: '日', 1: '一', 2: '二', 3: '三', 4: '四', 5: '五', 6: '六' }, krw: '₩',
  },
  ja: {
    storeLocationSelect: '店舗の場所を選択', prepTime: '準備時間', storeSelection: '店舗選択', cancel: 'キャンセル',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': '営業中', '준비중': '準備中', '마감': '閉店' },
    categories: { appetizers: '前菜', mains: 'メインディッシュ', sides: 'サイドメニュー', drinks: 'ドリンク', desserts: 'デザート', uncategorized: '未分類', all: 'すべて' },
    menuItems: {
      tacoClassico: { name: 'クラシックタコス', description: '新鮮な食材で作られた伝統的なメキシカンタコス' },
      tacoPollo: { name: 'チキンタコス', description: 'グリルチキンとアボカドのタコス' },
      nachosCheese: { name: 'チーズナチョス', description: 'カリカリのナチョスチップと濃厚なチーズソース' },
      guacamole: { name: 'グアカモーレ', description: '新鮮なアボカドのディップソース' },
      quesadilla: { name: 'ケサディーヤ', description: 'チーズとチキンの入ったメキシカンブレッド' },
      coke: { name: 'コカ・コーラ', description: '冷たい炭酸飲料' },
      agua: { name: '水', description: '浄水' },
      flan: { name: 'フラン', description: '伝統的なメキシカンキャラメルプリン' },
      burrito: { name: 'ブリトー', description: 'お米、豆、肉が入った大きなトルティーヤラップ' },
      salsa: { name: 'サルサ', description: '新鮮なトマトとスパイスのメキシカンソース' },
    },
    discount: '割引', addToCart: 'カートに入れる', viewCart: 'カートを見る', items: '個',
    days: { 0: '日曜日', 1: '月曜日', 2: '火曜日', 3: '水曜日', 4: '木曜日', 5: '金曜日', 6: '土曜日' },
    shortDays: { 0: '日', 1: '月', 2: '火', 3: '水', 4: '木', 5: '金', 6: '土' }, krw: '₩',
  },
  hi: {
    storeLocationSelect: 'स्टोर स्थान चुनें', prepTime: 'तैयारी का समय', storeSelection: 'स्टोर चयन', cancel: 'रद्द करें',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'खुला', '준비중': 'तैयारी कर रहा है', '마감': 'बंद' },
    categories: { appetizers: 'क्षुधावर्धक', mains: 'मुख्य व्यंजन', sides: 'साइड डिश', drinks: 'पेय', desserts: 'मिठाई', uncategorized: 'अवर्गीकृत', all: 'सभी' },
    menuItems: {
      tacoClassico: { name: 'क्लासिक टैको', description: 'ताजा सामग्री के साथ पारंपरिक मैक्सिकन टैको' },
      tacoPollo: { name: 'चिकन टैको', description: 'ग्रिल्ड चिकन और एवोकाडो वाला टैको' },
      nachosCheese: { name: 'चीज़ नाचोस', description: 'कुरकुरे नाचोस चिप्स के साथ समृद्ध चीज़ सॉस' },
      guacamole: { name: 'गुआकामोले', description: 'ताजा एवोकाडो डिप सॉस' },
      quesadilla: { name: 'क्वेसडिला', description: 'चीज़ और चिकन के साथ मैक्सिकन ब्रेड' },
      coke: { name: 'कोका-कोला', description: 'ठंडा कार्बोनेटेड पेय' },
      agua: { name: 'पानी', description: 'शुद्ध पानी' },
      flan: { name: 'फ्लान', description: 'पारंपरिक मैक्सिकन कारमेल पुडिंग' },
      burrito: { name: 'बुर्रिटो', description: 'चावल, बीन्स और मांस से भरा बड़ा टॉर्टिला रैप' },
      salsa: { name: 'साल्सा', description: 'ताजा टमाटर और मसालों की मैक्सिकन सॉस' },
    },
    discount: 'छूट', addToCart: 'कार्ट में जोड़ें', viewCart: 'कार्ट देखें', items: 'आइटम',
    days: { 0: 'रविवार', 1: 'सोमवार', 2: 'मंगलवार', 3: 'बुधवार', 4: 'गुरुवार', 5: 'शुक्रवार', 6: 'शनिवार' },
    shortDays: { 0: 'रवि', 1: 'सोम', 2: 'मंगल', 3: 'बुध', 4: 'गुरु', 5: 'शुक्र', 6: 'शनि' }, krw: '₩',
  },
  de: {
    storeLocationSelect: 'Standort auswählen', prepTime: 'Zubereitungszeit', storeSelection: 'Geschäftsauswahl', cancel: 'Abbrechen',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'Geöffnet', '준비중': 'Vorbereitung', '마감': 'Geschlossen' },
    categories: { appetizers: 'Vorspeisen', mains: 'Hauptgerichte', sides: 'Beilagen', drinks: 'Getränke', desserts: 'Desserts', uncategorized: 'Nicht kategorisiert', all: 'Alle' },
    menuItems: {
      tacoClassico: { name: 'Klassischer Taco', description: 'Traditioneller mexikanischer Taco mit frischen Zutaten' },
      tacoPollo: { name: 'Hähnchen-Taco', description: 'Taco mit gegrilltem Hähnchen und Avocado' },
      nachosCheese: { name: 'Käse-Nachos', description: 'Knusprige Nachos-Chips mit reichhaltiger Käsesauce' },
      guacamole: { name: 'Guacamole', description: 'Frische Avocado-Dip-Sauce' },
      quesadilla: { name: 'Quesadilla', description: 'Mexikanisches Brot mit Käse und Hähnchen' },
      coke: { name: 'Coca-Cola', description: 'Kaltes kohlensäurehaltiges Getränk' },
      agua: { name: 'Wasser', description: 'Gereinigtes Wasser' },
      flan: { name: 'Flan', description: 'Traditioneller mexikanischer Karamellpudding' },
      burrito: { name: 'Burrito', description: 'Große Tortilla gefüllt mit Reis, Bohnen und Fleisch' },
      salsa: { name: 'Salsa', description: 'Frische mexikanische Tomaten- und Gewürzsauce' },
    },
    discount: 'Rabatt', addToCart: 'In den Warenkorb', viewCart: 'Warenkorb anzeigen', items: 'Artikel',
    days: { 0: 'Sonntag', 1: 'Montag', 2: 'Dienstag', 3: 'Mittwoch', 4: 'Donnerstag', 5: 'Freitag', 6: 'Samstag' },
    shortDays: { 0: 'So', 1: 'Mo', 2: 'Di', 3: 'Mi', 4: 'Do', 5: 'Fr', 6: 'Sa' }, krw: '₩',
  },
  it: {
    storeLocationSelect: 'Seleziona posizione', prepTime: 'Tempo di preparazione', storeSelection: 'Selezione negozio', cancel: 'Annulla',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'Aperto', '준비중': 'In preparazione', '마감': 'Chiuso' },
    categories: { appetizers: 'Antipasti', mains: 'Piatti principali', sides: 'Contorni', drinks: 'Bevande', desserts: 'Dessert', uncategorized: 'Non categorizzato', all: 'Tutti' },
    menuItems: {
      tacoClassico: { name: 'Taco classico', description: 'Taco messicano tradizionale con ingredienti freschi' },
      tacoPollo: { name: 'Taco al pollo', description: 'Taco con pollo alla griglia e avocado' },
      nachosCheese: { name: 'Nachos al formaggio', description: 'Chips di nachos croccanti con ricca salsa al formaggio' },
      guacamole: { name: 'Guacamole', description: 'Salsa di avocado fresco' },
      quesadilla: { name: 'Quesadilla', description: 'Pane messicano con formaggio e pollo' },
      coke: { name: 'Coca-Cola', description: 'Bevanda gassata fredda' },
      agua: { name: 'Acqua', description: 'Acqua purificata' },
      flan: { name: 'Flan', description: 'Budino messicano tradizionale al caramello' },
      burrito: { name: 'Burrito', description: 'Grande tortilla ripiena di riso, fagioli e carne' },
      salsa: { name: 'Salsa', description: 'Salsa messicana fresca di pomodoro e spezie' },
    },
    discount: 'Sconto', addToCart: 'Aggiungi al carrello', viewCart: 'Vedi carrello', items: 'articoli',
    days: { 0: 'Domenica', 1: 'Lunedì', 2: 'Martedì', 3: 'Mercoledì', 4: 'Giovedì', 5: 'Venerdì', 6: 'Sabato' },
    shortDays: { 0: 'Dom', 1: 'Lun', 2: 'Mar', 3: 'Mer', 4: 'Gio', 5: 'Ven', 6: 'Sab' }, krw: '₩',
  },
  ru: {
    storeLocationSelect: 'Выбрать местоположение', prepTime: 'Время приготовления', storeSelection: 'Выбор магазина', cancel: 'Отмена',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'Открыто', '준비중': 'Подготовка', '마감': 'Закрыто' },
    categories: { appetizers: 'Закуски', mains: 'Основные блюда', sides: 'Гарниры', drinks: 'Напитки', desserts: 'Десерты', uncategorized: 'Без категории', all: 'Все' },
    menuItems: {
      tacoClassico: { name: 'Классическое тако', description: 'Традиционное мексиканское тако со свежими ингредиентами' },
      tacoPollo: { name: 'Куриное тако', description: 'Тако с жареной курицей и авокадо' },
      nachosCheese: { name: 'Сырные начос', description: 'Хрустящие чипсы начос с насыщенным сырным соусом' },
      guacamole: { name: 'Гуакамоле', description: 'Соус из свежего авокадо' },
      quesadilla: { name: 'Кесадилья', description: 'Мексиканский хлеб с сыром и курицей' },
      coke: { name: 'Кока-Кола', description: 'Холодный газированный напиток' },
      agua: { name: 'Вода', description: 'Очищенная вода' },
      flan: { name: 'Флан', description: 'Традиционный мексиканский карамельный пудинг' },
      burrito: { name: 'Буррито', description: 'Большая тортилья, наполненная рисом, бобами и мясом' },
      salsa: { name: 'Сальса', description: 'Свежий мексиканский томатно-пряный соус' },
    },
    discount: 'Скидка', addToCart: 'Добавить в корзину', viewCart: 'Просмотр корзины', items: 'шт.',
    days: { 0: 'Воскресенье', 1: 'Понедельник', 2: 'Вторник', 3: 'Среда', 4: 'Четверг', 5: 'Пятница', 6: 'Суббота' },
    shortDays: { 0: 'Вс', 1: 'Пн', 2: 'Вт', 3: 'Ср', 4: 'Чт', 5: 'Пт', 6: 'Сб' }, krw: '₩',
  },
  ar: {
    storeLocationSelect: 'اختر موقع المتجر', prepTime: 'وقت التحضير', storeSelection: 'اختيار المتجر', cancel: 'إلغاء',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'مفتوح', '준비중': 'جاري التحضير', '마감': 'مغلق' },
    categories: { appetizers: 'مقبلات', mains: 'الأطباق الرئيسية', sides: 'أطباق جانبية', drinks: 'مشروبات', desserts: 'حلويات', uncategorized: 'غير مصنف', all: 'الكل' },
    menuItems: {
      tacoClassico: { name: 'تاكو كلاسيكي', description: 'تاكو مكسيكي تقليدي بمكونات طازجة' },
      tacoPollo: { name: 'تاكو الدجاج', description: 'تاكو مع دجاج مشوي وأفوكادو' },
      nachosCheese: { name: 'ناتشوز بالجبن', description: 'رقائق ناتشوز مقرمشة مع صلصة جبن غنية' },
      guacamole: { name: 'جواكامولي', description: 'صلصة من الأفوكادو الطازج' },
      quesadilla: { name: 'كيساديا', description: 'خبز مكسيكي بالجبن والدجاج' },
      coke: { name: 'كوكاكولا', description: 'مشروب غازي بارد' },
      agua: { name: 'ماء', description: 'ماء نقي' },
      flan: { name: 'فلان', description: 'بودنغ كراميل مكسيكي تقليدي' },
      burrito: { name: 'بوريتو', description: 'تورتيلا كبيرة محشوة بالأرز والفاصوليا واللحم' },
      salsa: { name: 'صلصة', description: 'صلصة مكسيكية طازجة من الطماطم والتوابل' },
    },
    discount: 'خصم', addToCart: 'أضف إلى السلة', viewCart: 'عرض السلة', items: 'عناصر',
    days: { 0: 'الأحد', 1: 'الإثنين', 2: 'الثلاثاء', 3: 'الأربعاء', 4: 'الخميس', 5: 'الجمعة', 6: 'السبت' },
    shortDays: { 0: 'أحد', 1: 'إثن', 2: 'ثلا', 3: 'أرب', 4: 'خمي', 5: 'جمع', 6: 'سبت' }, krw: '₩',
  },
  vi: {
    storeLocationSelect: 'Chọn vị trí cửa hàng', prepTime: 'Thời gian chuẩn bị', storeSelection: 'Chọn cửa hàng', cancel: 'Hủy',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'Đang mở', '준비중': 'Đang chuẩn bị', '마감': 'Đã đóng' },
    categories: { appetizers: 'Khai vị', mains: 'Món chính', sides: 'Món phụ', drinks: 'Đồ uống', desserts: 'Tráng miệng', uncategorized: 'Chưa phân loại', all: 'Tất cả' },
    menuItems: {
      tacoClassico: { name: 'Taco cổ điển', description: 'Taco truyền thống Mexico với nguyên liệu tươi' },
      tacoPollo: { name: 'Taco gà', description: 'Taco với thịt gà nướng và bơ' },
      nachosCheese: { name: 'Nachos phô mai', description: 'Bánh nachos giòn với sốt phô mai đậm đà' },
      guacamole: { name: 'Guacamole', description: 'Sốt bơ tươi' },
      quesadilla: { name: 'Quesadilla', description: 'Bánh mì Mexico với phô mai và thịt gà' },
      coke: { name: 'Coca-Cola', description: 'Nước có ga lạnh' },
      agua: { name: 'Nước', description: 'Nước tinh khiết' },
      flan: { name: 'Flan', description: 'Bánh pudding caramel Mexico truyền thống' },
      burrito: { name: 'Burrito', description: 'Bánh tortilla lớn cuộn với cơm, đậu và thịt' },
      salsa: { name: 'Salsa', description: 'Sốt Mexico tươi từ cà chua và gia vị' },
    },
    discount: 'Giảm giá', addToCart: 'Thêm vào giỏ', viewCart: 'Xem giỏ hàng', items: 'mục',
    days: { 0: 'Chủ nhật', 1: 'Thứ hai', 2: 'Thứ ba', 3: 'Thứ tư', 4: 'Thứ năm', 5: 'Thứ sáu', 6: 'Thứ bảy' },
    shortDays: { 0: 'CN', 1: 'T2', 2: 'T3', 3: 'T4', 4: 'T5', 5: 'T6', 6: 'T7' }, krw: '₩',
  },
  th: {
    storeLocationSelect: 'เลือกตำแหน่งร้าน', prepTime: 'เวลาเตรียม', storeSelection: 'การเลือกร้าน', cancel: 'ยกเลิก',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'เปิด', '준비중': 'กำลังเตรียม', '마감': 'ปิด' },
    categories: { appetizers: 'อาหารเรียกน้ำย่อย', mains: 'จานหลัก', sides: 'เครื่องเคียง', drinks: 'เครื่องดื่ม', desserts: 'ของหวาน', uncategorized: 'ไม่จัดประเภท', all: 'ทั้งหมด' },
    menuItems: {
      tacoClassico: { name: 'ทาโก้คลาสสิก', description: 'ทาโก้เม็กซิกันแบบดั้งเดิมพร้อมส่วนผสมสด' },
      tacoPollo: { name: 'ทาโก้ไก่', description: 'ทาโก้กับไก่ย่างและอะโวคาโด' },
      nachosCheese: { name: 'นาโชชีส', description: 'นาโชกรอบกับซอสชีสเข้มข้น' },
      guacamole: { name: 'กัวคาโมเล', description: 'ซอสจิ้มอะโวคาโดสด' },
      quesadilla: { name: 'เคซาดีญา', description: 'ขนมปังเม็กซิกันกับชีสและไก่' },
      coke: { name: 'โคคา-โคล่า', description: 'เครื่องดื่มอัดลมเย็น' },
      agua: { name: 'น้ำ', description: 'น้ำบริสุทธิ์' },
      flan: { name: 'ฟลาน', description: 'พุดดิ้งคาราเมลเม็กซิกันแบบดั้งเดิม' },
      burrito: { name: 'เบอร์ริโต', description: 'แป้งทอร์ทิญาขนาดใหญ่ห่อด้วยข้าว ถั่ว และเนื้อ' },
      salsa: { name: 'ซัลซา', description: 'ซอสเม็กซิกันสดจากมะเขือเทศและเครื่องเทศ' },
    },
    discount: 'ส่วนลด', addToCart: 'เพิ่มลงตะกร้า', viewCart: 'ดูตะกร้า', items: 'รายการ',
    days: { 0: 'วันอาทิตย์', 1: 'วันจันทร์', 2: 'วันอังคาร', 3: 'วันพุธ', 4: 'วันพฤหัสบดี', 5: 'วันศุกร์', 6: 'วันเสาร์' },
    shortDays: { 0: 'อา', 1: 'จ', 2: 'อ', 3: 'พ', 4: 'พฤ', 5: 'ศ', 6: 'ส' }, krw: '₩',
  },
  nl: {
    storeLocationSelect: 'Locatie selecteren', prepTime: 'Bereidingstijd', storeSelection: 'Winkel selecteren', cancel: 'Annuleren',
    storeNames: { blueLunaCafe: 'Blue Luna Café', elPatioTacos: 'El Patio Tacos', laCasaCafe: 'La Casa del Café', rinconMexicano: 'Rincón Mexicano', panaderiaDulceVida: 'Panadería Dulce Vida', cantinaLaFiesta: 'Cantina La Fiesta', saborMexico: 'Sabor a México', tacoExpress: 'Taco Express', cafeAroma: 'Café Aroma', mercadoVerde: 'Mercado Verde' },
    storeStatus: { '영업중': 'Open', '준비중': 'In voorbereiding', '마감': 'Gesloten' },
    categories: { appetizers: 'Voorgerechten', mains: 'Hoofdgerechten', sides: 'Bijgerechten', drinks: 'Dranken', desserts: 'Desserts', uncategorized: 'Niet gecategoriseerd', all: 'Alle' },
    menuItems: {
      tacoClassico: { name: 'Klassieke Taco', description: 'Traditionele Mexicaanse taco met verse ingrediënten' },
      tacoPollo: { name: 'Kip Taco', description: 'Taco met gegrilde kip en avocado' },
      nachosCheese: { name: 'Kaas Nachos', description: 'Knapperige nacho-chips met rijke kaassaus' },
      guacamole: { name: 'Guacamole', description: 'Verse avocado dipsaus' },
      quesadilla: { name: 'Quesadilla', description: 'Mexicaans brood met kaas en kip' },
      coke: { name: 'Coca-Cola', description: 'Koud koolzuurhoudende drank' },
      agua: { name: 'Water', description: 'Gezuiverd water' },
      flan: { name: 'Flan', description: 'Traditionele Mexicaanse karamelpudding' },
      burrito: { name: 'Burrito', description: 'Grote tortilla gevuld met rijst, bonen en vlees' },
      salsa: { name: 'Salsa', description: 'Verse Mexicaanse tomaat- en kruidensaus' },
    },
    discount: 'Korting', addToCart: 'Toevoegen aan winkelwagen', viewCart: 'Winkelwagen bekijken', items: 'items',
    days: { 0: 'Zondag', 1: 'Maandag', 2: 'Dinsdag', 3: 'Woensdag', 4: 'Donderdag', 5: 'Vrijdag', 6: 'Zaterdag' },
    shortDays: { 0: 'Zo', 1: 'Ma', 2: 'Di', 3: 'Wo', 4: 'Do', 5: 'Fr', 6: 'Za' }, krw: '₩',
  },
};
